import React, { useState, useEffect } from 'react';
import { Download, FileSpreadsheet, Package, Truck, AlertTriangle, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMyContext } from '../../../context/MyContext';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import { Base_Url, forwardorderlistall } from '../../../config/config';
import { TbReportSearch } from "react-icons/tb";
import '../../../css/custom-scrollbar.css'
import { toast } from 'react-toastify';
import Papa from "papaparse";
import FullPageLoader from '../../../../src/component/fullPageLoader/fullPageLoader'

const REPORT_STYLES = {
  'MIS Report': { icon: FileSpreadsheet },
  'Order Product Report': { icon: Package },
  'Current Shipment Report': { icon: Truck },
  'NDR Report': { icon: AlertTriangle },
};

const ReportsDropdown = () => {
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const navigate = useNavigate();
  const [isPageLoading, isSetPageLoading] = useState();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRanges, setDateRanges] = useState({
    'MIS Report': { startDate: '', endDate: '' },
    'Order Product Report': { startDate: '', endDate: '' },
    'Current Shipment Report': { startDate: '', endDate: '' },
    'NDR Report': { startDate: '', endDate: '' }
  });

  const validateDate = (date) => {
    const selectedDate = new Date(date);
    const today = new Date();
    return selectedDate <= today;
  };

  const reports = [
    {
      name: 'MIS Report',
      description: 'Management information system report with detailed order',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'AWB Number': 'awb_number',
        'Pickup Name': 'pickup_name',
        'Pickup Address': 'pickup_address',
        'Pickup City': 'pickup_city',
        'Pickup Pin Code': 'pickup_pincode',
        'Pickup Country': 'pickup_country',
        'Pickup Phone': 'pickup_phone',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Drop Address': 'drop_address',
        'Drop Pin': 'drop_pin',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Phone': 'phone',
        'Email': 'email',
        'Payment Mode': 'payment_mode',
        'Total Amount': 'total_amount',
        'Created At': 'created_at',
        'Partner': 'partner',
        'Warehouse ID': 'warehouse_id',
        'Shipment Type': 'shipment_type',
        'Payment ID': 'payment_id',
        'Product Description': 'product_desc',
        'COD Amount': 'cod_amount',
        'Quantity': 'quantity',
        'Width': 'width',
        'Height': 'height',
        'Length': 'length',
        'Weight': 'weight',
        'Shipping Mode': 'shipping_mode',
        'Address Type': 'address_type',
        'Request ID': 'request_id',
        'Is NDR': 'is_ndr',
        'Status': 'status',
        'Debit Amount': 'debit_amount'
      }
    },
    {
      name: 'Order Product Report',
      description: 'Detailed product information for all orders',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Product Description': 'product_desc',
        'Quantity': 'quantity',
        'SKU': 'sku',
        'AWB Number': 'awb_number',
        'Created At': 'created_at'
      }
    },
    {
      name: 'Current Shipment Report',
      description: 'Real-time status of all active shipments',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Product Description': 'product_desc',
        'Quantity': 'quantity',
        'SKU': 'sku',
        'AWB Number': 'awb_number',
        'Drop Address': 'drop_address',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Drop Pin': 'drop_pin',
        'Status': 'status'
      }
    },
    {
      name: 'NDR Report',
      description: 'Non-delivery report with attempt details and reasons',
      columnMapping: {
        'Order ID': 'order_id',
        'First Name': 'first_name',
        'User ID': 'user_id',
        'Last Name': 'last_name',
        'Phone': 'phone',
        'Product Description': 'product_desc',
        'AWB Number': 'awb_number',
        'Drop Address': 'drop_address',
        'Drop Pin': 'drop_pin',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Status': 'status',
        'NDR Attempt': 'ndr_attempt',
        'Updated At': 'updated_at',
        'NDR Reason': 'ndr_reason'
      }
    }
  ];

  useEffect(() => {
    fetchOrderData();
  }, []);

  const fetchOrderData = async () => {
    setLoading(true);
    setError(null);
    isSetPageLoading(true);
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    try {
      const response = await fetch(Base_Url + forwardorderlistall, {
        method: 'GET',
        headers: {
          token,
          cus_id: cusid,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();

      if (response.ok) {
        setData(result.data || []);
        isSetPageLoading(false);
      } else {
        setError(result.message);
        isSetPageLoading(false);
      }
    } catch (error) {
      setError('Failed to fetch data. Please try again later.');
      isSetPageLoading(false);
    } finally {
      setLoading(false);
      isSetPageLoading(false);
    }
  };

  const handleDateChange = (reportName, field, value) => {
    if (value && !validateDate(value)) {
      toast.error('Future dates are not allowed');
      return;
    }

    if (field === 'endDate' && value) {
      const startDate = dateRanges[reportName].startDate;
      if (startDate && new Date(value) < new Date(startDate)) {
        toast.error('End date cannot be before start date');
        return;
      }
    }

    if (field === 'startDate' && value) {
      const endDate = dateRanges[reportName].endDate;
      if (endDate && new Date(value) > new Date(endDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }

    setDateRanges(prev => ({
      ...prev,
      [reportName]: {
        ...prev[reportName],
        [field]: value
      }
    }));
  };

  const isDownloadDisabled = (reportName) => {
    const { startDate, endDate } = dateRanges[reportName];
    return loading || !startDate || !endDate;
  };

  const downloadCSV = async (report) => {
    const { startDate, endDate } = dateRanges[report.name];

    let filteredData = data;
    if (startDate && endDate) {
      filteredData = data.filter(item => {
        const itemDate = new Date(item.created_at);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return itemDate >= start && itemDate <= end;
      });
    }

    if (filteredData.length === 0) {
      toast.error('No data found for the selected date range');
      return;
    }

    const csvData = filteredData.map(item => {
      const row = {};
      Object.entries(report.columnMapping).forEach(([displayName, apiKey]) => {
        row[displayName] = item[apiKey] !== undefined && item[apiKey] !== null ? item[apiKey] : 'N/A';
      });
      return row;
    });

    try {
      const csv = Papa.unparse(csvData, {
        header: true,
        quotes: true,
        quoteChar: '"',
        escapeChar: '"'
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${report.name} downloaded successfully!`);
    } catch (error) {
      console.error('Error generating CSV:', error);
      toast.error('Failed to generate CSV file');
    }
  };

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-gray-50'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div className={`fixed z-20 h-full w-64 md:relative duration-300 bg-white dark:bg-black dark:text-white md:bg-transparent md:w-64 md:z-20 border-r border-gray-200 dark:border-gray-800`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )}
        <FullPageLoader isLoading={isPageLoading} message="Processing your request..." />
        
        <div className="flex-1 dark:bg-black p-3 md:p-4 w-full bg-gray-200 overflow-y-auto custom-scrollbar">
          <div className="max-w-6xl mx-auto">

            {/* Header */}
            <div className="flex-shrink-0 mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <TbReportSearch size={16} className="text-gray-700 dark:text-gray-300" />
                </div>
                <div>
                  <h1 className="text-base font-bold text-gray-800 dark:text-white tracking-tight">Reports Dashboard</h1>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500">Select date range and export reports</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-[11px] text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {/* Reports Grid */}
            <div className="grid gap-3 md:grid-cols-2">
              {reports.map((report, idx) => {
                const style = REPORT_STYLES[report.name];
                const Icon = style.icon;
                const disabled = isDownloadDisabled(report.name);

                return (
                  <div
                    key={report.name}
                    className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 hover:shadow-md transition-all"
                  >
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                        <Icon size={15} className="text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <h2 className="text-[11px] font-bold text-gray-800 dark:text-white uppercase tracking-wider">
                          {report.name}
                        </h2>
                        <p className="text-[9px] text-gray-400 dark:text-gray-500">{report.description}</p>
                        <p className="text-[8px] text-gray-400 dark:text-gray-500 mt-0.5">
                          {Object.keys(report.columnMapping).length} fields
                        </p>
                      </div>
                    </div>

                    {/* Date Inputs */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="block text-[8px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          Start Date
                        </label>
                        <div className="relative">
                          <Calendar size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            value={dateRanges[report.name].startDate}
                            onChange={(e) => handleDateChange(report.name, 'startDate', e.target.value)}
                            className="w-full pl-7 pr-2 py-1.5 text-[10px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500 transition-all"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[8px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                          End Date
                        </label>
                        <div className="relative">
                          <Calendar size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            value={dateRanges[report.name].endDate}
                            onChange={(e) => handleDateChange(report.name, 'endDate', e.target.value)}
                            className="w-full pl-7 pr-2 py-1.5 text-[10px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500 transition-all"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Download Button */}
                    <button
                      onClick={() => downloadCSV(report)}
                      disabled={disabled}
                      className={`w-full py-1.5 text-[10px] font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                        disabled
                          ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                          : 'bg-gray-800 dark:bg-white text-white dark:text-gray-800 hover:opacity-85'
                      }`}
                    >
                      <Download size={12} />
                      Download CSV
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsDropdown;